// Common API types

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errorCode?: string;
}

// Paginated response type
export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Repair statuses
export enum RepairStatus {
  Received = 'Received',
  InDiagnosis = 'InDiagnosis',
  AwaitingApproval = 'AwaitingApproval',
  WaitingForParts = 'WaitingForParts',
  InRepair = 'InRepair',
  // 'QualityCheck' removed 2026-08-11 - boss's explicit call: never used, no wired-up way to
  // reach it. Backend RepairStatus keeps the numeric gap; nothing here needs to reference it.
  ReadyForPickup = 'ReadyForPickup',
  Delivered = 'Delivered',
  Cancelled = 'Cancelled',
  Unrepairable = 'Unrepairable',
}

// Status labels in Hebrew (keys match the backend RepairStatus enum names)
export const RepairStatusLabels: Record<RepairStatus, string> = {
  [RepairStatus.Received]: 'התקבל למעבדה',
  [RepairStatus.InDiagnosis]: 'באבחון',
  [RepairStatus.AwaitingApproval]: 'ממתין לאישור',
  [RepairStatus.WaitingForParts]: 'ממתין לחלקים',
  [RepairStatus.InRepair]: 'בתיקון',
  [RepairStatus.ReadyForPickup]: 'מוכן לאיסוף',
  [RepairStatus.Delivered]: 'נמסר',
  [RepairStatus.Cancelled]: 'בוטל',
  [RepairStatus.Unrepairable]: 'לא ניתן לתיקון',
};

// Device types
export enum DeviceType {
  Smartphone = 1,
  Tablet = 2,
  Laptop = 3,
  Desktop = 4,
  Smartwatch = 5,
  Console = 6,
  Other = 99,
}

export const DeviceTypeLabels: Record<DeviceType, string> = {
  [DeviceType.Smartphone]: 'סמארטפון',
  [DeviceType.Tablet]: 'טאבלט',
  [DeviceType.Laptop]: 'מחשב נייד',
  [DeviceType.Desktop]: 'מחשב נייח',
  [DeviceType.Smartwatch]: 'שעון חכם',
  [DeviceType.Console]: 'קונסולה',
  [DeviceType.Other]: 'אחר',
};

// Physical condition
export enum PhysicalCondition {
  Perfect = 1,
  Minor = 2,
  Moderate = 3,
  Severe = 4,
  Critical = 5,
}

// Wording matches the backend enum's [Description] attributes (PhysicalCondition.cs), which is what
// the ticket page displays. There used to be three different vocabularies for this one field - these
// labels ("מושלם/קל/בינוני/חמור/קריטי"), a second set inside PhysicalConditionAssessment, and the
// backend's grades - so a technician who picked "קריטי" at intake later read
// "לא פעיל - לחלקים בלבד" on the ticket and could not tell it was the same answer.
//
// Short form for the picker buttons (five to a row), full form for reading. The short label is a
// prefix of the full one on purpose, so the two are recognisably the same value.
export const PhysicalConditionLabels: Record<PhysicalCondition, string> = {
  [PhysicalCondition.Perfect]: 'מצוין',
  [PhysicalCondition.Minor]: 'טוב',
  [PhysicalCondition.Moderate]: 'סביר',
  [PhysicalCondition.Severe]: 'גרוע',
  [PhysicalCondition.Critical]: 'לא פעיל',
};

// Accessories are stored by their internal value ("charger"), so any screen that shows them has to
// translate back. The ticket page used to print the raw list, so a technician who ticked "מטען" at
// intake read "charger" afterwards. Kept here rather than in the intake page so both ends share it.
export const AccessoryLabels: Record<string, string> = {
  charger: 'מטען',
  cable: 'כבל',
  case: 'כיסוי',
  screen_protector: 'מגן מסך',
  earphones: 'אוזניות',
  stylus: 'עט',
  sim_tray: 'מגש SIM',
  box: 'קופסה מקורית',
};

/** Falls back to the raw value so an accessory added later still shows something. */
export const accessoryLabel = (value: string): string => AccessoryLabels[value] ?? value;

export const PhysicalConditionDescriptions: Record<PhysicalCondition, string> = {
  [PhysicalCondition.Perfect]: 'מצוין - ללא פגמים',
  [PhysicalCondition.Minor]: 'טוב - שריטות קלות',
  [PhysicalCondition.Moderate]: 'סביר - פגמים נראים',
  [PhysicalCondition.Severe]: 'גרוע - נזק משמעותי',
  [PhysicalCondition.Critical]: 'לא פעיל - לחלקים בלבד',
};

// Payment methods - numbers MUST match the backend's PaymentMethod enum; they're sent as numbers
// (they used to differ from 3 up, so ביט was saved as PayMe and an העברה as installments - 2026-10-19).
export enum PaymentMethod {
  Cash = 1,
  CreditCard = 2,
  CreditInstallments = 3,
  Bit = 4,
  PayMe = 5,
  BankTransfer = 6,
  Check = 7,
}

export const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: 'מזומן',
  [PaymentMethod.CreditCard]: 'כרטיס אשראי',
  [PaymentMethod.CreditInstallments]: 'אשראי בתשלומים',
  [PaymentMethod.Bit]: 'ביט',
  [PaymentMethod.PayMe]: 'פיימי',
  [PaymentMethod.BankTransfer]: 'העברה בנקאית',
  [PaymentMethod.Check]: "צ'ק",
};

// What staff can pick when taking a payment - the business doesn't accept checks (her call, 2026-10-19).
export const SELECTABLE_PAYMENT_METHODS: PaymentMethod[] = [
  PaymentMethod.Cash,
  PaymentMethod.CreditCard,
  PaymentMethod.BankTransfer,
  PaymentMethod.Bit,
  PaymentMethod.PayMe,
];

// Urgency levels - used for ticket-creation requests (frontend-only concept during intake)
export enum UrgencyLevel {
  Low = 1,
  Normal = 2,
  High = 3,
  Urgent = 4,
}

export const UrgencyLabels: Record<UrgencyLevel, string> = {
  [UrgencyLevel.Low]: 'נמוך',
  [UrgencyLevel.Normal]: 'רגיל',
  [UrgencyLevel.High]: 'גבוה',
  [UrgencyLevel.Urgent]: 'דחוף',
};

// Ticket priority - what the backend RepairPriority enum actually serializes as (string names,
// matching Program.cs's JsonStringEnumConverter). This is the field present on ticket responses;
// UrgencyLevel above is a different, numeric, request-only concept.
export enum RepairPriority {
  Low = 'Low',
  Normal = 'Normal',
  High = 'High',
  Urgent = 'Urgent',
}

export const RepairPriorityLabels: Record<RepairPriority, string> = {
  [RepairPriority.Low]: 'נמוכה',
  [RepairPriority.Normal]: 'רגילה',
  [RepairPriority.High]: 'גבוהה',
  [RepairPriority.Urgent]: 'דחופה',
};

// Money type
// Mirrors the backend's MoneyResponse. `formatted` is the server-rendered display string
// ("₪450.00"); it is always sent, so it's declared here even though the UI currently formats
// amounts itself.
export interface Money {
  amount: number;
  currency: string;
  formatted?: string;
}

// Phone number type
export interface PhoneNumber {
  value: string;
  formatted: string;
}

// Address type - mirrors the backend's AddressResponse exactly. Every field is optional because
// the backend declares them all nullable. `country` used to be declared here as a required string,
// but the API has never sent it and nothing in the app ever read it; `fullAddress` is the
// pre-formatted single-line version the backend composes.
export interface Address {
  street?: string;
  city?: string;
  zipCode?: string;
  fullAddress?: string;
}

// Base entity fields
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt?: string;
}

// Query parameters for pagination
export interface PaginationParams {
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}
